import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  UntypedFormControl,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Language } from '@shared/utils/common.util';
import { MemoDetail, MemoList } from '../../model/memo.model';
import { MemoService } from '../../service/memo.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertService } from '@core/services/alert.service';
import { Subscription, forkJoin } from 'rxjs';

@Component({
  selector: 'app-memo-remind-modal',
  templateUrl: './memo-remind-modal.component.html',
  styleUrls: ['./memo-remind-modal.component.scss'],
  standalone: false,
})
export class MemoRemindModalComponent implements OnInit {
  @Input() memo: MemoList;
  @Output() closeModal = new EventEmitter<MemoDetail>();

  form: FormGroup;
  languages: any = [];
  loaMemberIdList: string[] = [];
  subscription: Subscription[] = [];
  typeReminder: 'approval_reminder' | 'urgent_approval' =
    'urgent_approval';

  private activeModal = inject(NgbActiveModal);

  constructor(
    private translate: TranslateService,
    private service: MemoService,
    private alert: AlertService,
  ) {}

  ngOnInit(): void {
    this.initDataForm();
    this.getDropdownLanguage();
  }

  initDataForm() {
    this.form = new FormGroup({
      remind: new FormControl(null),
      notification_language: new FormControl('default'),
    });
  }

  close() {
    this.activeModal.close();
    this.closeModal.emit();
  }

  onTypeReminder(
    event: 'approval_reminder' | 'urgent_approval' | any,
  ) {
    this.typeReminder = event;
  }

  setSubjectPlaceholder(): void {
    if (this.selectDefault) {
      this.form
        .get('email_subject')
        ?.setValue(this.subjectPlaceholder);
    }
  }

  sendReminder() {
    if (!this.loaMemberIdList.length) {
      this.alert.errorSubText(
        this.translate.instant('ALERT.Saving unsuccessful'),
        this.translate.instant(
          'ALERT.Please check the information again',
        ),
        {
          iconPath:
            'assets/images/icons/marks/x-circle-fill.svg#x-circle-fill',
          width: 30,
          height: 30,
          x: 24,
          y: 20,
        },
      );
      return;
    }
    const notification_language =
      this.form.value.notification_language;
    const data = {
      type: this.typeReminder,
      to_list: this.loaMemberIdList,
      detail: this.form.value.remind,
      notification_language:
        notification_language === 'default'
          ? null
          : notification_language,
    };
    const subscription = this.service
      .sendReminder(this.memo.id, data)
      .subscribe({
        next: () => {
          this.loaMemberIdList = [];
          this.form.reset({
            remind: null,
            notification_language: 'default',
          });
          this.activeModal.close();
          this.alert.success(
            this.translate.instant('COMMON.Remind sent successfully'),
          );
        },
        error: (error) => {
          this.alert.error(error.error.to_list);
          this.alert.error(error.error.type);
        },
      });
    this.subscription.push(subscription);
  }

  getDropdownLanguage(): void {
    forkJoin([
      this.translate.get('LANGUAGE.DEFAULT'),
      this.translate.get('LANGUAGE.THAI'),
      this.translate.get('LANGUAGE.ENGLISH'),
    ]).subscribe((res: string[]) => {
      this.languages = [
        { label: res[0], value: 'default' },
        { label: res[1], value: Language.th },
        { label: res[2], value: Language.en },
      ];
    });
  }

  get detailControl(): UntypedFormControl {
    return this.form?.get('remind') as UntypedFormControl;
  }

  get selectDefault() {
    return this.form?.get('selectDefault')?.value || false;
  }

  get subjectPlaceholder(): string {
    const notification_language = this.form?.get(
      'notification_language',
    )?.value;
    const prefix =
      notification_language === 'default'
        ? this.translate.instant(
            'EMAIL-SETTING.New Approval Request:',
          )
        : notification_language === Language.th
          ? 'คำขออนุมัติเอกสาร: '
          : 'New Approval Request: ';
    return `${prefix} ${
      this.memo.subject ||
      this.translate.instant('EMAIL-SETTING.MEMO-SUBJECT')
    }`;
  }
}
